// @flow
import type { User } from '~/app/types';
import React, { useState } from 'react';
import Button from '~/app/components/Button';
import { flashMessage as __flashMessage } from '~/app/actions';
import injectSheet from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { gql, useMutation } from '@apollo/client';
import RolePanels from './RolePanels';
import LdapInput from './LdapInput';

type Props = {
  classes: {
    [key: string]: string
  },
  isFetching: boolean,
  categories: Object,
  update: (id: string, user: User) => Promise<*>,
}

const AddUser = (props: Props) => {
  const { classes: c } = props;

  const dispatch = useDispatch();
  const flashMessage = (message, type) => dispatch(__flashMessage(message, type));

  const { user }: { user: User } = useSelector(mapStateToProps);

  const [draft, setDraft] = useState({
    username: '',
    email: '',
    createdBy: user.sAMAccountName,
    updatedBy: '',
    isSuperAdmin: false,
    isActive: false,
    roles: {
      categories: [],
      others: []
    }
  });

  // addUser
  const [__addUser] = useMutation(ACTIONS, {
    refetchQueries: ['getUsers']
  });
  const addUser = (user) => __addUser({
    variables: { user }
  });

  const onUpdateRolePanels = (roles) => {
    setDraft(x => ({
      ...x,
      roles
    }));
  }

  const handleSelectUsername = ({ username, email }) => {
    setDraft(x => ({
      ...x,
      username,
      email
    }));
  }

  const handleClickAdmin = () => {
    setDraft(x => ({
      ...x,
      isSuperAdmin: !x.isSuperAdmin
    }));
  }

  const handleClickActive = () => {
    setDraft(x => ({
      ...x,
      isActive: !x.isActive
    }));
  }

  /**
   * Add user to the database.
   */
  const handleAddUser = async () => {
    if (!draft.username) {
      flashMessage('Please select username.', 'ERROR');
      return;
    }

    try {
      await addUser(draft);
      flashMessage('Successfully saved user!');
    } catch (e) {
      flashMessage(e.message, 'ERROR');
    }
  }

  const { isSuperAdmin, isActive } = draft;

  return (
    <div className={c.container}>
      <div className={c.title}>Add User</div>
      <div className={c.row}>
        <div className={c.labelContainer}>LDAP Username</div>
        <div className={c.flexFive}>
          <LdapInput onUpdate={handleSelectUsername} />
        </div>
      </div>
      <div className={c.row}>
        <div className={c.labelContainer}>Admin</div>
        <div className={c.flexFive}>
          <input
            type="checkbox"
            checked={!!isSuperAdmin}
            onChange={handleClickAdmin}
          />
        </div>
      </div>
      <div className={c.row}>
        <div className={c.labelContainer}>Active</div>
        <div className={c.flexFive}>
          <input
            type="checkbox"
            checked={!!isActive}
            onChange={handleClickActive}
          />
        </div>
      </div>
      <div className={c.row}>
        <div className={c.labelContainer}>Roles</div>
        <div className={c.flexFive}>
          <RolePanels onUpdate={onUpdateRolePanels} />
        </div>
      </div>
      <div className={c.row}>
        <div className={c.labelContainer} />
        <div className={c.flexFive}>
          <Button primary onClick={handleAddUser}>Add</Button>
        </div>
      </div>
    </div>
  );
}

/* =============================================================================
=    GraphQL
============================================================================= */
const ACTIONS = gql`
mutation addUser($user: AddUserInput) {
  addUser(user: $user) {
    username
    email
    createdBy
    updatedBy
    isSuperAdmin
    isActive
    roles {
      categories {
        category
        roleType
      }
      others {
        category
        roleType
      }
    }
  }
}
`;

/* =============================================================================
=    CSS
============================================================================= */
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: '20px 30px',
    fontSize: '12px'
  },
  title: {
    fontSize: '1rem'
  },
  row: {
    display: 'flex',
    padding: '5px 0'
  },
  labelContainer: {
    flex: 1,
    textAlign: 'right',
    paddingRight: '15px',
    fontWeight: 'bold'
  },
  flexFive: {
    flex: 5
  }
};

/* =============================================================================
=    Redux
============================================================================= */
const mapStateToProps = state => ({
  user: state.app.user
});

export default injectSheet(styles)(AddUser);
